<template>
  <div class="material-info">
    <InfoTitle title="动画参数" />
    <LabelSlider label="透明度" :value="effectInfo.opacity" type="opacity" />
    <LabelSlider
      label="亮度"
      :value="effectInfo.brightness"
      type="brightness"
      :step="0.01"
    />
    <LabelInput
      label="旋转"
      :deg="true"
      v-model="currentEffect.content.rotate"
      :precision="0"
    />
    <LabelSwitch label="循环播放" v-model="currentEffect.content.loop" />
    <position-editor :effect="currentEffect" />
    <label-button
      text="适应画布"
      :disabled="currentEffect.container"
      @click="fitCanvas"
    >
      大小
    </label-button>
    <label-button text="截取" @click="showCropModal">截取和定格&hellip;</label-button>
    <template v-if="!currentEffect.container">
      <Divider />
      <InfoTitle title="动画转场效果" />
      <template v-if="!currentEffect.container">
        <LabelInput
          label="延迟出现（秒）"
          :time="true"
          v-model="displayStart"
        />
        <LabelInput
          label="显示时长（秒）"
          :time="true"
          v-model="displayDuration"
        />
      </template>
      <LabelOption
        label="入场效果"
        :optionList="enterEffectList"
        v-model="currentEffect.enter"
      />
      <LabelInput
        label="入场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.enterDuration"
        :disabled="currentEffect.enter === 'none'"
      />
      <LabelOption
        label="出场效果"
        :optionList="leaveEffectList"
        v-model="currentEffect.leave"
      />
      <LabelInput
        label="出场效果显示时长（秒）"
        :time="true"
        v-model="currentEffect.leaveDuration"
        :disabled="currentEffect.leave === 'none'"
      />
      <!-- <a-button
      block
      class="toggle-button"
      @click="selectContainer"
      v-if="currentEffect.container"
    >选择容器</a-button>-->
      <Divider />
      <InfoTitle title="动态效果" />
      <LabelSwitch
        label="动态特效"
        v-model="currentEffect.showTracks"
        :fn="handleTrackShow"
      />
      <SingleEffectEditor
        label="特效"
        v-for="(track, i) in currentEffect.tracks"
        :disabled="!currentEffect.showTracks"
        :key="i"
        :track="track"
        :effectList="effectListAt(i)"
        @delete="removeTrack(i)"
        :deletable="currentEffect.tracks.length > 1"
      />
      <a-button
        v-if="currentEffect.tracks.length < 5"
        class="insert-track"
        @click="insertTrack"
        block
        :disabled="!currentEffect.showTracks"
        icon="plus"
        >继续添加</a-button
      >
    </template>
    <lottie-crop-modal
      :visible.sync="cropShowing"
      :src="effectInfo.src"
      :value="displayCrop"
      @change="handleCropChange"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import LabelInput from "../base/LabelInput";
import LabelOption from "../base/LabelOption";
import LabelSwitch from "../base/LabelSwitch";
import LabelSlider from "../base/LabelSlider";
import LabelButton from '../base/LabelButton';
import InfoTitle from "../base/InfoTitle";
import Divider from '../base/Divider';
import SingleEffectEditor from '../base/SingleEffectEditor';
import PositionEditor from '../base/PositionEditor';
import { effectParams } from '../../utils/textEffectMap';
import { fitCanvas } from '../../utils/util';
import LottieCropModal from './LottieCropModal';
import { clamp } from '../../utils';

export default {
  name: "MaterialInfo",
  components: {
    LabelInput,
    LabelOption,
    LabelSwitch,
    LabelSlider,
    InfoTitle,
    Divider,
    SingleEffectEditor,
    PositionEditor,
    LabelButton,
    LottieCropModal
  },
  data: function () {
    return {
      opacity: 1,
      light: 1,
      cropShowing: false,
      enterEffectList: [
        { name: '无', value: 'none' },
        { name: '淡入', value: 'enter' }
      ],
      leaveEffectList: [
        { name: '无', value: 'none' },
        { name: '淡出', value: 'enter' }
      ]
    };
  },
  computed: {
    ...mapGetters(['activeFragment', 'effectInfo', 'currentEffect']),
    ...mapState(['effectNames', 'generalEffects', 'screenRatio']),
    screenWidth() {
      return 480 * this.screenRatio;
    },
    displayStart: {
      get() {
        return this.currentEffect.start;
      },
      set(value) {
        this.$set(this.currentEffect, 'start', Math.max(value, 0));
      }
    },
    displayDuration: {
      get() {
        return this.currentEffect.duration;
      },
      set(value) {
        this.$set(this.currentEffect, 'duration', Math.max(value, 400));
      }
    },
    displayCrop() {
      const { content: efc, duration, container } = this.currentEffect;
      const containerEffect = container && this.effects[container];
      const usedDuration = containerEffect ? containerEffect.duration : duration;

      if (efc.crop) {
        return efc.crop;
      }
      return [0, usedDuration / 1e3];
    }
  },
  methods: {
    ...mapActions(['setEditStatus']),
    onChange: function (value) {
      console.log('changed', value);
    },
    handleTrackShow(value) {
      if (value) {
        this.$set(this.currentEffect.tracks[0], 'start', this.currentEffect.start);
      }
    },
    insertTrack() {
      let effectType = '';
      for (var i of this.generalEffects) {
        if (this.currentEffect.tracks.every(tr => {
          switch (tr.effectType) {
            case 'slide':
            case 'slideHorizon':
              return i !== 'slide' && i !== 'slideHorizon';
          }
          return i !== tr.effectType;
        })) {
          effectType = i;
          break;
        }
      }
      if (effectType) {
        this.currentEffect.tracks.push({
          id: +new Date() + '',
          effectType,
          params: effectParams[effectType] ? { ...effectParams[effectType] } : null,
          start: 1e3,
          duration: 1e3
        });
      } else {
        console.log('No effect was added');
      }
    },
    removeTrack(i) {
      this.$set(
        this.currentEffect,
        'tracks',
        this.currentEffect.tracks.slice(0, i).concat(this.currentEffect.tracks.slice(i + 1))
      )
    },
    effectListAt(i) {
      return this.generalEffects
        .map(v => {
          let disabled = false;
          switch (v) {
            case 'slide':
            case 'slideHorizon':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && (tr.effectType === 'slide' || tr.effectType === 'slideHorizon'));
              break;
            case 'scale':
            case 'gradientFade':
            case 'gradientHide':
              disabled = this.currentEffect.tracks.some((tr, j) =>
                j !== i && tr.effectType === v);
              break;
          }
          return {
            name: this.effectNames[v],
            value: v,
            disabled
          };
        });
    },
    /**
     * fit the image within the canvas' size.
     */
    fitCanvas() {
      const efc = this.currentEffect.content;
      const { top, left, width, height } = fitCanvas(efc, this.screenWidth, 480);

      efc.height = height;
      efc.width = width;
      efc.top = top;
      efc.left = left;
    },
    showCropModal() {
      this.cropShowing = true;
    },
    setDuration(duration) {
      this.$set(this.currentEffect, 'duration', duration);

      if (this.currentEffect.container) {
        const container = this.effects[this.currentEffect.container];
        if (container) {
          this.$set(container, 'duration', clamp(container.duration, 400, duration));
        }
      }
    },
    handleCropChange({ start, end, total }) {
      const efc = this.currentEffect.content;
      this.$set(efc, 'crop', [start, end]);
    }
  },
  beforeDestroy() {
    this.setEditStatus(false)
  }
}
</script>

<style scoped>
.material-info {
  padding: 11px 12px 11px 13px;
  background: rgba(248, 248, 248, 1);
}

.setting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}

.label {
  font-size: 12px;
  font-weight: 400;
  /* color: rgba(101, 102, 103, 1); */
}

.toggle-button {
  margin-top: 12px;
  height: 36px;
  border-radius: 2px;
}

.insert-track {
  border-color: #e5e5e7;
  background: #fff;
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.75);
  border-radius: 2px;
}

.insert-track:hover,
.insert-track:focus {
  border-color: #ccc;
}

.insert-track:disabled {
  border-color: #dcdcdc;
  background: #fff;
  color: rgba(0, 0, 0, 0.75);
  opacity: 0.35;
}
</style>

